import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import { useThemeStudio } from "@/features/theming/theme-studio-context";
import type { ThemeCandidate } from "@/data/theme-engine";

/** A chip-less mini render of a generated theme — a little app screen, not a
 *  swatch — so the partner reads it as a full look. Click to apply it live. */
function CandidateCard({ candidate, active, onPick }: {
  candidate: ThemeCandidate;
  active: boolean;
  onPick: () => void;
}) {
  const c = candidate.hexes;
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-[14px] border text-left outline-none transition-all focus-visible:ring-[3px] focus-visible:ring-ring",
        active ? "border-brand ring-2 ring-brand/40" : "border-border hover:border-border-strong",
      )}
    >
      {/* Mini app screen */}
      <div className="p-2.5" style={{ background: c.background }}>
        <div
          className="mb-2 flex items-center gap-1.5 rounded-[8px] px-2 py-1.5"
          style={{ background: c.header }}
        >
          <span className="size-2 rounded-full" style={{ background: c.accent }} />
          <span className="text-[10px] font-bold" style={{ color: c.text }}>
            {candidate.label}
          </span>
        </div>
        <div className="mb-2 rounded-[8px] p-2" style={{ background: c.surface }}>
          <div className="mb-1.5 h-1.5 w-3/4 rounded-full" style={{ background: c.text, opacity: 0.8 }} />
          <div className="h-1.5 w-1/2 rounded-full" style={{ background: c.text, opacity: 0.4 }} />
        </div>
        <div
          className="rounded-[8px] py-1.5 text-center text-[10px] font-bold"
          style={{ background: c.primary, color: c.background }}
        >
          Play
        </div>
      </div>

      {/* Caption */}
      <div className="flex items-center justify-between gap-1.5 border-t border-border bg-panel-2/60 px-2.5 py-2">
        <div className="min-w-0">
          <div className="truncate text-[12px] font-semibold">{candidate.label}</div>
          <div className="truncate text-[10.5px] text-text-3">{candidate.spec}</div>
        </div>
        {active && (
          <span className="grid size-4 shrink-0 place-items-center rounded-full bg-brand text-white">
            <Check className="size-3" />
          </span>
        )}
      </div>
    </button>
  );
}

/** The generated candidates row — 2-3 complete themes. Empty until the partner
 *  generates from something (the ThemeGenerator above). */
export function ThemeCandidates() {
  const { candidates, recipe, recipeActive, applyCandidate } = useThemeStudio();

  if (candidates.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="mb-2 text-[13px] font-semibold">Generated themes</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {candidates.map((cand) => (
          <CandidateCard
            key={cand.id}
            candidate={cand}
            active={recipeActive && recipe.treatmentId === cand.recipe.treatmentId && recipe.seed === cand.recipe.seed}
            onPick={() => applyCandidate(cand)}
          />
        ))}
      </div>
    </div>
  );
}
